import {useEffect} from "react";
import {useNavigate} from "react-router-dom";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import {styled} from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import PropTypes from "prop-types";

const Arrow = styled("div")(({theme}) => ({
    position: "absolute",
    top: -6,
    right: 16,
    width: 12,
    height: 12,
    background: "white",
    transform: "rotate(45deg)",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.1)",
    zIndex: -1,
}));

function ProfileDropdown({openProfileMenu, anchorEl, setAnchorEl, loggedInUserDetail, handleLogOut}) {
    const navigate = useNavigate();

    // Close dropdown on Escape key
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === "Escape") setAnchorEl(null);
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [setAnchorEl]);

    return (
        <Popper
            open={openProfileMenu}
            anchorEl={anchorEl}
            placement="bottom-end"
            modifiers={[
                {
                    name: "offset",
                    options: {offset: [0, 10]}
                },
                {
                    name: "preventOverflow",
                    options: {
                        boundary: "viewport",
                        padding: 8,
                    },
                },
                {
                    name: "flip",
                    options: {
                        fallbackPlacements: ["bottom-start", "top-end", "top-start"],
                    },
                },
            ]}
            style={{zIndex: 1400}} // Ensure it's above other elements
            transition
        >
            {({TransitionProps}) => (
                <Fade in={openProfileMenu} timeout={200} {...TransitionProps}>
                    <div>
                        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                            <Box sx={{position: "relative"}}>
                                <Arrow sx={{
                                    display: {xs: 'none', sm: 'block'} // Hide arrow on mobile for cleaner look
                                }}/>
                                <SoftBox
                                    p={{xs: 1.5, sm: 2}}
                                    minWidth={{xs: 280, sm: 250}}
                                    maxWidth={{xs: 320, sm: 300}}
                                    sx={{
                                        background: "linear-gradient(to bottom, rgba(255,255,255,0.97), rgba(245,245,245,0.97))",
                                        backdropFilter: "blur(6px)",
                                        borderRadius: {xs: 3, sm: 2},
                                        border: "1px solid rgba(0,0,0,0.1)",
                                        boxShadow: {
                                            xs: "0px 8px 32px rgba(0,0,0,0.25)", // Stronger shadow on mobile
                                            sm: "0px 4px 20px rgba(0,0,0,0.2)"
                                        },
                                        // Prevent dropdown from going off-screen on mobile
                                        transform: {
                                            xs: 'translateX(-50%)', // Center horizontally on mobile
                                            sm: 'none'
                                        },
                                        right: {
                                            xs: '50%',
                                            sm: 'auto'
                                        }
                                    }}
                                >
                                    {/* User Info */}
                                    <SoftTypography
                                        variant="h6"
                                        sx={{
                                            fontSize: {xs: '1rem', sm: '1.25rem'},
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {loggedInUserDetail?.firstName} {loggedInUserDetail?.lastName}
                                    </SoftTypography>
                                    <SoftTypography
                                        variant="body2"
                                        sx={{
                                            fontSize: {xs: '0.8rem', sm: '0.875rem'},
                                            wordBreak: 'break-word' // Handle long emails on mobile
                                        }}
                                    >
                                        {loggedInUserDetail?.email}
                                    </SoftTypography>
                                    <SoftTypography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: {xs: '0.8rem', sm: '0.875rem'}
                                        }}
                                    >
                                        Role: {loggedInUserDetail?.userRole}
                                    </SoftTypography>

                                    {/* Divider */}
                                    <Divider sx={{my: {xs: 1.5, sm: 2}}}/>

                                    {/* Profile Button */}
                                    <SoftButton
                                        color="primary"
                                        fullWidth
                                        sx={{
                                            mb: 1,
                                            py: {xs: 1, sm: 0.75},
                                            fontSize: {xs: '0.9rem', sm: '0.875rem'}
                                        }}
                                        onClick={() => {
                                            setAnchorEl(null);
                                            navigate("/profile");
                                        }}
                                    >
                                        Profile
                                    </SoftButton>

                                    {/* Logout Button */}
                                    <SoftButton
                                        color="error"
                                        fullWidth
                                        sx={{
                                            py: {xs: 1, sm: 0.75},
                                            fontSize: {xs: '0.9rem', sm: '0.875rem'}
                                        }}
                                        onClick={() => {
                                            setAnchorEl(null);
                                            handleLogOut();
                                        }}
                                    >
                                        Logout
                                    </SoftButton>
                                </SoftBox>
                            </Box>
                        </ClickAwayListener>
                    </div>
                </Fade>
            )}
            {/*<SoftBox*/}
            {/*    p={2}*/}
            {/*    minWidth={250}*/}
            {/*    sx={{*/}
            {/*        background: "linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(245,245,245,0.95))", // Soft gradient*/}
            {/*        backdropFilter: "blur(6px)",  // Frosted glass effect*/}
            {/*        borderRadius: 2,*/}
            {/*        border: "1px solid rgba(0,0,0,0.1)", // Subtle border*/}
            {/*        boxShadow: "0px 4px 20px rgba(0,0,0,0.15)", // Stronger shadow for contrast*/}
            {/*        zIndex: 1300,*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <SoftTypography variant="h6">*/}
            {/*        {loggedInUserDetail?.firstName} {loggedInUserDetail?.lastName}*/}
            {/*    </SoftTypography>*/}
            {/*    <SoftTypography variant="body2">{loggedInUserDetail?.email}</SoftTypography>*/}
            {/*    <SoftTypography variant="body2">*/}
            {/*        {loggedInUserDetail?.userRole}*/}
            {/*    </SoftTypography>*/}
            {/*    <Divider sx={{ my: 2 }} />*/}
            {/*    <SoftButton*/}
            {/*        color="primary"*/}
            {/*        fullWidth*/}
            {/*        sx={{ mb: 1 }}*/}
            {/*        onClick={() => {*/}
            {/*            setAnchorEl(null);  // Close dropdown*/}
            {/*            navigate("/profile");  // Navigate to profile page*/}
            {/*        }}*/}
            {/*    >*/}
            {/*        Profile*/}
            {/*    </SoftButton>*/}
            {/*    <SoftButton*/}
            {/*        color="error"*/}
            {/*        fullWidth*/}
            {/*        onClick={() => {*/}
            {/*            setAnchorEl(null);  // Close dropdown*/}
            {/*            handleLogOut().then();     // Trigger logout*/}
            {/*        }}*/}
            {/*    >*/}
            {/*        Logout*/}
            {/*    </SoftButton>*/}
            {/*</SoftBox>*/}
        </Popper>
    );
}

ProfileDropdown.propTypes = {
    openProfileMenu: PropTypes.bool.isRequired,
    anchorEl: PropTypes.any, // can be a DOM element or null
    setAnchorEl: PropTypes.func.isRequired,
    loggedInUserDetail: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        userRole: PropTypes.string,
        profilePic: PropTypes.string,
    }),
    handleLogOut: PropTypes.func.isRequired,
};

export default ProfileDropdown;
